import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  listUpcomingReservations,
  adminConfirmReservation,
  searchUsersClient,
  getAllUsersBasic,
} from "./reservationsApi";

import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, RefreshCw, Plus } from "lucide-react";

import ConfirmDialog from "../../../components/common/ConfirmDialog";
import FormButton from "../../../components/form/FormButton";
import FormInput from "../../../components/form/FormInput";

import "./upcomingReservations.css";

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const ymd = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "—");

function millisToParts(ms) {
  if (ms == null || ms <= 0) return { h: 0, m: 0, s: 0, done: true };

  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  return { h, m, s, done: false };
}

function useTick(ms = 1000) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
}

const Badge = ({ type, children }) => (
  <span className={`up-badge up-badge--${type}`}>{children}</span>
);

const UpcomingReservations = () => {
  const nav = useNavigate();
  const location = useLocation();

  const basePath = location.pathname.startsWith("/venue-admin")
    ? "/venue-admin"
    : location.pathname.startsWith("/country-admin")
    ? "/country-admin"
    : "/super-admin";

  const [userId, setUserId] = useState("");
  const [userLabel, setUserLabel] = useState("");
  const [qUser, setQUser] = useState("");
  const [userChoices, setUserChoices] = useState([]);
  const [showDrop, setShowDrop] = useState(false);

  const [recentUsers, setRecentUsers] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sa_upcoming_recent_users") || "[]");
    } catch {
      return [];
    }
  });

  const [from, setFrom] = useState(() => ymd(new Date()));
  const [to, setTo] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 45);
    return ymd(d);
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    pendingConfirmation: [],
    confirmed: [],
  });

  const [busyId, setBusyId] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  useTick(1000);

  useEffect(() => {
    getAllUsersBasic().catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      const q = qUser.trim();

      if (!q) {
        setUserChoices([]);
        return;
      }

      try {
        const items = await searchUsersClient(q);
        if (alive) setUserChoices(Array.isArray(items) ? items : []);
      } catch {
        if (alive) setUserChoices([]);
      }
    };

    const t = setTimeout(run, 200);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [qUser]);

  const load = async () => {
    if (!userId) {
      toast.info("Enter a UserId to load upcoming reservations");
      return;
    }

    setLoading(true);

    try {
      const res = await listUpcomingReservations(userId, from, to);
      const loadedAt = Date.now();

      const pending = Array.isArray(res?.pendingConfirmation)
        ? res.pendingConfirmation.map((item) => ({ ...item, _loadedAt: loadedAt }))
        : [];

      const confirmed = Array.isArray(res?.confirmed) ? res.confirmed : [];

      setData({
        pendingConfirmation: pending,
        confirmed,
      });

      if (userId) {
        const entry = {
          id: userId,
          label:
            userLabel ||
            `${res.user?.name || "—"}${res.user?.phone ? " • " + res.user.phone : ""}`,
        };

        setRecentUsers((prev) => {
          const filtered = prev.filter((p) => p.id !== entry.id);
          const next = [entry, ...filtered].slice(0, 6);
          localStorage.setItem("sa_upcoming_recent_users", JSON.stringify(next));
          return next;
        });
      }

      if (!pending.length && !confirmed.length) {
        toast.info("No upcoming reservations in this range");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || "Failed to load upcoming reservations",
      );
    } finally {
      setLoading(false);
    }
  };

  const openConfirm = (row) => {
    if (!row?.reservationId) return;
    setConfirmTarget(row);
  };

  const handleConfirmReservation = async () => {
    const id = confirmTarget?.reservationId;
    if (!id) return;

    setBusyId(id);

    try {
      await adminConfirmReservation(id);
      toast.success("Reservation confirmed");
      setConfirmTarget(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Confirm failed");
    } finally {
      setBusyId(null);
    }
  };

  const payNow = () => {
    toast.info("Pay Now is a stub here. Wire to your payment page if needed.");
  };

  const BookingCard = ({ row, kind }) => {
    const remainingMs =
      kind === "pending" && typeof row.timeRemainingMs === "number"
        ? row.timeRemainingMs - (Date.now() - Number(row._loadedAt || Date.now()))
        : null;

    const { h, m, s, done } = millisToParts(remainingMs);

    const userLine = row.user
      ? `${row.user.name || "—"}${row.user.phone ? " · " + row.user.phone : ""}`
      : "—";

    const slotStr = Array.isArray(row.timeSlots)
      ? row.timeSlots.map((t) => `${t.start} – ${t.end}`).join(", ")
      : "—";

    const dateTimeLine = `${row.date || "—"} · ${slotStr}`;

    const deadlineInfo =
      row.holdType === "recurring"
        ? row.paymentDeadline
          ? new Date(row.paymentDeadline).toLocaleString()
          : "—"
        : row.reservationExpiresAt
        ? new Date(row.reservationExpiresAt).toLocaleString()
        : "—";

    const venueLine = `${row.venueName || "—"} · ${row.groundName || "—"}`;

    const badge =
      kind === "confirmed" ? (
        <Badge type="confirmed">Confirmed</Badge>
      ) : done ? (
        <Badge type="expired">Hold expired</Badge>
      ) : (
        <Badge type="action">Waiting for payment</Badge>
      );

    return (
      <div className="up-booking-card">
        {badge}

        <div className="up-line">
          <strong>{venueLine}</strong>
        </div>

        <div className="up-line up-muted">{userLine}</div>

        <div className="up-line">{dateTimeLine}</div>

        {typeof row.price !== "undefined" && row.price !== null && (
          <div className="up-line">{fmtINR(row.price)}</div>
        )}

        {kind === "pending" && (
          <div className="up-helper">
            {row.holdType === "recurring" ? (
              <>
                Pay before <strong>{deadlineInfo}</strong>
              </>
            ) : (
              <>
                Expires at <strong>{deadlineInfo}</strong>
              </>
            )}
          </div>
        )}

        {kind === "pending" && (
          <div className="up-countdown-row">
            <span className={`up-countdown ${done ? "is-done" : ""}`}>
              {done
                ? "Expired"
                : `${String(h).padStart(2, "0")}:${String(m).padStart(
                    2,
                    "0",
                  )}:${String(s).padStart(2, "0")}`}
            </span>
          </div>
        )}

        {kind === "pending" ? (
          <div className="up-card-actions">
            {done ? (
              <FormButton
                type="button"
                variant="secondary"
                className="up-btn-disabled"
                disabled
              >
                No action available
              </FormButton>
            ) : (
              <>
                <FormButton
                  type="button"
                  variant="primary"
                  onClick={() => payNow(row)}
                  disabled={busyId === row.reservationId}
                >
                  Pay
                </FormButton>

                <FormButton
                  type="button"
                  variant="primary"
                  onClick={() => openConfirm(row)}
                  disabled={busyId === row.reservationId}
                  title="Admin confirm (bypass payment)"
                >
                  Confirm (Admin)
                </FormButton>
              </>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div className="up-page">
        <div className="up-container">
          <div className="up-topbar">
            <FormButton
              type="button"
              variant="secondary"
              className="up-back"
              onClick={() => nav(-1)}
            >
              <ArrowLeft size={16} /> Back
            </FormButton>
          </div>

          <h1 className="up-title">Upcoming Bookings</h1>

          <div className="up-subtitle">
            Bookings scheduled within the selected date range
          </div>

          <div className="up-filter-card">
            <div className="up-filter-title">Filter bookings</div>

            <div className="up-filters">
              <div className="up-user-wrap">
                <FormInput
                  className="up-input"
                  placeholder="Search by player name or phone number"
                  value={qUser}
                  onFocus={() => setShowDrop(true)}
                  onChange={(e) => {
                    const next = e.target.value;

                    setQUser(next);
                    setShowDrop(true);

                    if (!next.trim()) {
                      setUserId("");
                      setUserLabel("");
                    }
                  }}
                  onBlur={() => setTimeout(() => setShowDrop(false), 150)}
                />

                {showDrop && userChoices.length > 0 && (
                  <div className="up-dropdown">
                    {userChoices.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        className="up-dd-item"
                        onMouseDown={() => {
                          setUserId(u.id);
                          setUserLabel(
                            `${u.name}${u.phone ? " • " + u.phone : ""}`,
                          );
                          setQUser(
                            `${u.name}${u.phone ? " • " + u.phone : ""}${
                              u.email ? " • " + u.email : ""
                            }`,
                          );
                          setShowDrop(false);
                        }}
                        title={u.email}
                      >
                        <span className="up-dd-title">{u.name || "—"}</span>
                        <span className="up-dd-sub">
                          {[u.phone, u.email].filter(Boolean).join(" • ")}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {recentUsers.length > 0 && (
                  <div className="up-recents">
                    {recentUsers.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        className={`up-chip ${userId === r.id ? "is-active" : ""}`}
                        title={r.id}
                        onClick={() => {
                          setUserId(r.id);
                          setUserLabel(r.label);
                          setQUser(r.label);
                        }}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <FormInput
                type="date"
                className="up-input"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />

              <FormInput
                type="date"
                className="up-input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />

              <FormButton
                type="button"
                variant="primary"
                className="up-load-btn"
                onClick={load}
                disabled={loading}
                title="Load Bookings"
              >
                <RefreshCw size={16} />
                {loading ? "Loading…" : "Load Bookings"}
              </FormButton>
            </div>

            <div className="up-create-hold">
              <FormButton
                type="button"
                variant="secondary"
                className="up-create-hold-btn"
                onClick={() =>
                  nav(
                    `${basePath}/reservations/recurring${
                      userId
                        ? `?userId=${encodeURIComponent(
                            userId,
                          )}&user=${encodeURIComponent(userLabel || qUser)}`
                        : ""
                    }`,
                  )
                }
                title="Create weekly recurring hold"
              >
                <Plus size={16} /> Create Temporary Hold
              </FormButton>

              <div className="up-hold-helper">Block a slot without immediate payment</div>
            </div>
          </div>

          <div className="up-two-col">
            <div className="up-section">
              <h2>Needs Action</h2>

              <div className="up-section-sub">
                Bookings waiting for payment or admin confirmation
              </div>

              {loading ? (
                <div className="up-booking-card">Loading…</div>
              ) : (data.pendingConfirmation || []).length === 0 ? (
                <div className="up-booking-card">No bookings need action.</div>
              ) : (
                data.pendingConfirmation.map((r) => (
                  <BookingCard key={r.reservationId} row={r} kind="pending" />
                ))
              )}
            </div>

            <div className="up-section">
              <h2>Confirmed Bookings</h2>

              <div className="up-section-sub">Guaranteed upcoming bookings</div>

              {loading ? (
                <div className="up-booking-card">Loading…</div>
              ) : (data.confirmed || []).length === 0 ? (
                <div className="up-booking-card">
                  No confirmed bookings in this range.
                </div>
              ) : (
                data.confirmed.map((r) => (
                  <BookingCard key={r.reservationId} row={r} kind="confirmed" />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmTarget}
        title="Confirm Held Reservation"
        message="Are you sure you want to confirm this held reservation now? This will bypass payment confirmation."
        confirmText="Confirm Reservation"
        cancelText="Cancel"
        confirmVariant="primary"
        loading={busyId === confirmTarget?.reservationId}
        onConfirm={handleConfirmReservation}
        onCancel={() => {
          if (busyId !== confirmTarget?.reservationId) {
            setConfirmTarget(null);
          }
        }}
      />
    </>
  );
};

export default UpcomingReservations;